import classNames from 'classnames';
import Hint from 'components/styleguide/hint/Hint';
import Icon from 'components/styleguide/icon/Icon';
import React, { useState } from 'react';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./Checkbox.module.scss');

export interface Props {
  autoSaveUrl?: string;
  checked?: boolean;
  children?: React.ReactNode;
  className?: string;
  context?: 'talent' | 'company' | 'neutral';
  disabled?: boolean;
  error?: string;
  id?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: string | JSX.Element;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, name?: string, value?: boolean) => void;
  required?: boolean;
  value?: string;
}

const Checkbox: React.FC<Props> = ({
  autoSaveUrl,
  checked,
  children,
  className,
  context = 'neutral',
  disabled,
  error,
  id,
  inputRef,
  label,
  name,
  onChange,
  required,
  value,
}) => {
  const [resolvedId] = useState(() => id || `checkbox-${Math.random().toString(36).slice(2, 9)}`);
  const { save } = useAutoSave({ url: autoSaveUrl || '', name: name || '', debounceMs: 0 });
  const isControlled = onChange !== undefined;
  const [internalChecked, setInternalChecked] = useState(checked ?? false);

  const resolvedChecked = isControlled ? checked : internalChecked;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    if (!isControlled) {
      setInternalChecked(newValue);
    }
    if (autoSaveUrl) save(newValue ? '1' : '0');
    if (onChange) onChange(event, name, newValue);
  };

  const wrapperClass = classNames(
    styles['ui-checkbox'],
    styles[`ui-checkbox--${context}`],
    className,
  );

  const boxClass = classNames(
    styles['ui-checkbox__box'],
    {
      [styles['ui-checkbox__box--invalid']]: !!error,
      [styles['ui-checkbox__box--disabled']]: disabled,
    },
  );

  const errorId = error ? `${resolvedId}-error` : undefined;

  return (
    <div className={wrapperClass}>
      <label className={styles['ui-checkbox__container']} htmlFor={resolvedId}>
        {name && <input type="hidden" name={name} value="0" />}
        <input
          aria-describedby={errorId}
          aria-invalid={Boolean(error)}
          checked={isControlled ? resolvedChecked : undefined}
          className={styles['ui-checkbox__input']}
          defaultChecked={!isControlled ? checked : undefined}
          disabled={disabled}
          id={resolvedId}
          name={name}
          onChange={handleChange}
          ref={inputRef}
          required={required}
          type="checkbox"
          value={value || '1'}
        />
        <span className={styles['ui-checkbox__box-halo']} aria-hidden="true">
          <span className={boxClass}>
            {resolvedChecked && <Icon name="check" color="neutral-border-white" size="sm" />}
          </span>
        </span>
        {label && (
          <span className={styles['ui-checkbox__label']}>
            {typeof label === 'string'
              ? <span dangerouslySetInnerHTML={{ __html: label }} />
              : label}
          </span>
        )}
      </label>
      {children && <div className={styles['ui-checkbox__children']}>{children}</div>}
      {error && !resolvedChecked && (
        <Hint id={errorId} variant="error" message={error} />
      )}
    </div>
  );
};

export default Checkbox;
